import React, { memo, useEffect, useRef, useState } from "react";

const tabs = [
  { id: "configuration", label: "Configuration" },
  { id: "sizes", label: "Sizes" },
];

export const ProductMarkWorkspaceHeader = memo(function ProductMarkWorkspaceHeader({
  activeTab,
  defaultTab = "configuration",
  onTabChange,
  onBack,
  canImport,
  onImport,
  printNode,
  onSave,
  saving,
  selectedSizeCount = 0,
  onBulkDeleteSelectedRows,
  availableColumns = [],
  visibleColumns = [],
  pendingColumns = [],
  onPendingColumnsChange,
  onApplyColumns,
  onResetColumns,
}) {
  const orderedTabs = tabs;
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const columnsMenuRef = useRef(null);
  const selectAllRef = useRef(null);
  const allSelected = availableColumns.length > 0 && pendingColumns.length === availableColumns.length;
  const someSelected = pendingColumns.length > 0 && !allSelected;
  const filteredColumns = availableColumns.filter((column) =>
    column.label.toLowerCase().includes(columnSearch.trim().toLowerCase())
  );

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  useEffect(() => {
    if (!columnsOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (!columnsMenuRef.current?.contains(event.target)) setColumnsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [columnsOpen]);

  const toggleColumn = (key) => {
    onPendingColumnsChange(
      pendingColumns.includes(key)
        ? pendingColumns.filter((columnKey) => columnKey !== key)
        : [...pendingColumns, key]
    );
  };

  const toggleColumnsMenu = () => {
    if (!columnsOpen) {
      onPendingColumnsChange(visibleColumns);
      setColumnSearch("");
    }
    setColumnsOpen((open) => !open);
  };

  return (
    <div className="product-mark-workspace-header mt-1">
      <div className="product-mark-workspace-left">
        <button
          type="button"
          className="product-mark-back"
          title="Back"
          aria-label="Back"
          onClick={onBack}
        >
          <i className="fas fa-angle-left"></i>
        </button>

        <div className="product-mark-tabs-wrapper">
  <div
    className="product-mark-tabs"
    role="tablist"
    aria-label="Product mark steps"
  >
    {orderedTabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        className={`product-mark-tab${activeTab === tab.id ? " active" : ""}`}
        aria-selected={activeTab === tab.id}
        onClick={() => onTabChange(tab.id)}
      >
        <span>{tab.label}</span>
      </button>
    ))}
  </div>

{activeTab === "sizes" && selectedSizeCount > 0 && (
  <span
    style={{
      fontSize: "15px",
      fontWeight: "600",
      color: "#0d6efd",
      whiteSpace: "nowrap",
      marginLeft: "14px"
    }}
  >
    {selectedSizeCount} Selected
  </span>
)}
</div>

      </div>

      <div className="product-mark-workspace-actions">
          <div className="product-mark-columns-menu" ref={columnsMenuRef}>
          <button
            className="btn product-mark-secondary-btn"
            type="button"
            onClick={toggleColumnsMenu}
            aria-expanded={columnsOpen}
            aria-haspopup="dialog"
            disabled={availableColumns.length === 0}
          >
            <i className="fas fa-cog" aria-hidden="true"></i>
          </button>
          {columnsOpen && (
            <div className="product-mark-columns-popover" role="dialog" aria-label="Column Visibility">
              <div className="product-mark-columns-title">
                <strong>Column Visibility</strong>
                <button type="button" aria-label="Close column visibility" onClick={() => setColumnsOpen(false)}>&times;</button>
              </div>
              <label className="product-mark-columns-search">
                <i className="fas fa-search" aria-hidden="true"></i>
                <input type="search" value={columnSearch} onChange={(event) => setColumnSearch(event.target.value)} placeholder="Search columns..." />
              </label>
              <div className="product-mark-column-option product-mark-column-option--all">
                <span>Select All</span>
                <div className="custom-control custom-switch product-mark-column-switch">
                  <input
                    ref={selectAllRef}
                    id="product-mark-select-all-columns"
                    className="custom-control-input"
                    type="checkbox"
                    checked={allSelected}
                    aria-checked={someSelected ? "mixed" : allSelected}
                    onChange={(event) => onPendingColumnsChange(event.target.checked ? availableColumns.map((column) => column.key) : [])}
                  />
                  <label className="custom-control-label" htmlFor="product-mark-select-all-columns" aria-label="Show all columns" />
                </div>
              </div>
              <div className="product-mark-columns-list">
                {filteredColumns.map((column) => (
                  <div className="product-mark-column-option" key={column.key}>
                    <span>{column.label}</span>
                    <div className="custom-control custom-switch product-mark-column-switch">
                      <input
                        id={`product-mark-column-${column.key}`}
                        className="custom-control-input"
                        type="checkbox"
                        checked={pendingColumns.includes(column.key)}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <label className="custom-control-label" htmlFor={`product-mark-column-${column.key}`} aria-label={`Show ${column.label} column`} />
                    </div>
                  </div>
                ))}
                {filteredColumns.length === 0 && <p className="product-mark-columns-empty">No columns found.</p>}
              </div>
              <div className="product-mark-columns-footer">
                <button type="button" className="product-mark-columns-reset" onClick={onResetColumns}><i className="fas fa-undo mr-1"></i>Reset</button>
                <button type="button" className="btn product-mark-save-btn" onClick={() => { onApplyColumns(); setColumnsOpen(false); }}>Apply</button>
              </div>
            </div>
          )}
          </div>
        {canImport && (
          <button className="btn product-mark-secondary-btn" type="button" onClick={onImport}>
            <i className="fas fa-file-import"></i>
            <span>Import</span>
          </button>
        )}
        {printNode}
       
        <button
          className="btn product-mark-save-btn"
          type="button"
          onClick={onSave}
          disabled={saving}
        >
          <i className="fas fa-save"></i>
          <span>Save</span>
        </button>
        {selectedSizeCount > 0 && (
         <button
          className="btn product-mark-danger-btn"
          type="button"
          onClick={onBulkDeleteSelectedRows}
          disabled={selectedSizeCount === 0}
          title={selectedSizeCount > 0 ? `Delete ${selectedSizeCount} selected rows` : "Select rows to delete"}
          aria-label={selectedSizeCount > 0 ? `Delete ${selectedSizeCount} selected rows` : "Delete selected rows"}
          style={{
            minWidth: "65px",
            minHeight: "20px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#dc3545",
            border: "1px solid #f0a5ad",
            background: "#fff5f6",
            borderRadius: "7px",
            height: "28px",
            width: "41px",
            gap: "6px",
           padding: "0, 12px",
          }}
        >
          <i className="fas fa-trash" style={{fontSize: "12px"}}></i>
          <span style={{fontSize: "12px", fontWeight: "800"}}>Delete</span>
        </button>
        )}
      </div>
    </div>
  );
});
